import {Auth} from 'aws-amplify'
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  Dispatch,
  SetStateAction,
  FC,
} from 'react'
import {
  clearSessionAnchor,
  ensureSessionAnchorIfMissing,
  isSessionExpiredByPolicy,
  syncSessionPreferenceFromCognito,
} from '../../../../shared/utils/sessionExpirationPreference'
import {CognitoUser} from 'amazon-cognito-identity-js'
import {toast} from 'react-toastify'
import {getLogger} from '../../../App'
import {toastDefaultConfig} from '../../../../shared/config/toastConfig'
import {clearUserSessionLocalStorage} from '../../../../shared/utils/userSessionStorage'
import * as authHelper from './AuthHelpers'
import {mapCognitoAttributesToUserAttributes, UserAttributes, CustomCognitoUser} from './_models'

type AuthContextProps = {
  cognitoAuth: CognitoUser | undefined
  saveCognitoAuth: (auth: CustomCognitoUser | undefined) => void
  currentUser: UserAttributes | undefined
  setCurrentUser: Dispatch<SetStateAction<UserAttributes | undefined>>
  cognitoLogout: () => void
  handleTokenExpiration: () => void
  verificationCode: string | undefined
  setVerificationCode: Dispatch<SetStateAction<string | undefined>>
  confirmSignUp: boolean | undefined
  setConfirmSignUp: Dispatch<SetStateAction<boolean | undefined>>
}

const AuthContext = createContext<AuthContextProps>({
  cognitoAuth: undefined,
  saveCognitoAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  cognitoLogout: () => {},
  handleTokenExpiration: () => {},
  verificationCode: undefined,
  setVerificationCode: () => {},
  confirmSignUp: undefined,
  setConfirmSignUp: () => {},
})

const useCognitoAuth = () => {
  return useContext(AuthContext)
}

const CognitoAuthProvider: FC<{children?: React.ReactNode; currentUserInit?: UserAttributes}> = ({
  children,
  currentUserInit,
}) => {
  const [cognitoAuth, setCognitoAuth] = useState<CognitoUser | undefined>(
    authHelper.getCognitoAuth()
  )
  const [currentUser, setCurrentUserState] = useState<UserAttributes | undefined>(currentUserInit)
  const [verificationCode, setVerificationCode] = useState<string | undefined>(undefined)
  const [confirmSignUp, setConfirmSignUp] = useState<boolean | undefined>(undefined)

  // Clear Results tabs and last Files path when the authenticated identity changes
  // (logout or a different user). Same-user refresh keeps those keys so the UI restores.
  const setCurrentUser: Dispatch<SetStateAction<UserAttributes | undefined>> = useCallback(
    (action) => {
      setCurrentUserState((prev) => {
        const next = typeof action === 'function' ? action(prev) : action
        // Only clear when switching to a *different* signed-in user. Do not clear on
        // undefined → user (auth init / refresh) or user → undefined (logout already clears).
        if (prev?.identity_id && next?.identity_id && prev.identity_id !== next.identity_id) {
          clearUserSessionLocalStorage()
        }
        return next
      })
    },
    []
  )

  const saveCognitoAuth = (auth: CustomCognitoUser | undefined) => {
    setCognitoAuth(auth)
    if (auth) {
      authHelper.setCognitoAuth(auth)
    } else {
      authHelper.removeCognitoAuth()
    }
  }

  const logout = async () => {
    try {
      if (cognitoAuth) {
        await Auth.signOut()
      }
    } catch (error) {
      getLogger().error('Error during logout:', error)
    } finally {
      // Clear session UI state before dropping auth so a remount cannot restore it
      clearUserSessionLocalStorage()
      localStorage.removeItem('ai-chat-last-conversation')
      clearSessionAnchor()

      saveCognitoAuth(undefined)
      setCurrentUser(undefined)
      authHelper.removeCognitoAuth()
      authHelper.removeHostedUIAuth()

      window.location.href = '/signin'
    }
  }

  const handleTokenExpiration = () => {
    getLogger().warn('Token expired, logging out user')
    logout()
  }

  return (
    <AuthContext.Provider
      value={{
        cognitoAuth,
        saveCognitoAuth,
        currentUser,
        setCurrentUser,
        cognitoLogout: logout,
        handleTokenExpiration,
        verificationCode,
        setVerificationCode,
        confirmSignUp,
        setConfirmSignUp,
      }}
    >
      <SessionPolicyMonitor />
      {children}
    </AuthContext.Provider>
  )
}

const SessionPolicyMonitor: FC = () => {
  const {cognitoAuth, cognitoLogout} = useCognitoAuth()
  const logoutRef = useRef(cognitoLogout)
  logoutRef.current = cognitoLogout

  useEffect(() => {
    if (!cognitoAuth) {
      return
    }
    const tick = () => {
      if (isSessionExpiredByPolicy()) {
        getLogger().warn('Session ended due to profile session expiration preference')
        logoutRef.current()
      }
    }
    tick()
    const id = window.setInterval(tick, 60_000)
    return () => window.clearInterval(id)
  }, [cognitoAuth])

  return null
}

const CognitoAuthInit: FC<{children?: React.ReactNode}> = ({children}) => {
  const {setCurrentUser, saveCognitoAuth} = useCognitoAuth()
  const didRequest = useRef(false)
  const [isInitializing, setIsInitializing] = useState(true)
  


  useEffect(() => {
    const requestCognitoUser = async () => {
      try {
        if (!didRequest.current) {
          didRequest.current = true

          getLogger().debug('Starting authentication check...')

          // Add a timeout to prevent hanging
          const timeoutPromise = new Promise(
            (_, reject) => setTimeout(() => reject(new Error('Auth timeout')), 3000) // Increased timeout
          )

          const authPromise = Auth.currentAuthenticatedUser()
          const cognitoUser: CognitoUser = (await Promise.race([
            authPromise,
            timeoutPromise,
          ])) as CognitoUser

          getLogger().debug('Authentication successful, mapping user attributes...')
          const userAttributes = await mapCognitoAttributesToUserAttributes(cognitoUser)
          syncSessionPreferenceFromCognito(userAttributes.sessionExpirationHours)

          setCurrentUser(userAttributes)
          saveCognitoAuth(cognitoUser)
          ensureSessionAnchorIfMissing()
          getLogger().debug('User authentication state updated successfully')
        }
      } catch (error) {
        if (!didRequest.current) {
          didRequest.current = true
          
          getLogger().warn('Authentication check failed:', error)
          // Don't call cognitoLogout here as it causes redirect loops
          // Just clear the auth state without redirecting
          saveCognitoAuth(undefined)
          setCurrentUser(undefined)
          authHelper.removeCognitoAuth()
          authHelper.removeHostedUIAuth()
        }

        // Only show error toast for actual errors, not timeouts
        if (error instanceof Error && error.message !== 'Auth timeout') {
          toast(error.message, {...toastDefaultConfig, type: 'error'})
          getLogger().error(error)
        }
      } finally {
        setIsInitializing(false)
        getLogger().debug('Authentication initialization completed')
      }
    }

    // Check if we have any auth data in localStorage first
    const hasStoredAuth = authHelper.getCognitoAuth() || authHelper.getHostedUIAuth()
    getLogger().debug('Stored auth check:', {hasStoredAuth})

    if (hasStoredAuth) {
      requestCognitoUser()
    } else {
      // If no stored auth, we're definitely not authenticated
      getLogger().debug('No stored auth found, user is not authenticated')
      setIsInitializing(false)
    }
  }, [setCurrentUser, saveCognitoAuth])

  // Show loading state while initializing auth
  if (isInitializing) {
    return <div>Loading...</div>
  }

  return <>{children}</>
}

export {CognitoAuthProvider, CognitoAuthInit, useCognitoAuth}
